import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  createPortfolio,
  deleteHolding,
  deletePortfolio,
  getCircuitBreakerStatus,
  getPortfolioKelly,
  getPortfolioRisk,
  getPortfolioSummary,
  getRebalanceProposal,
  listPortfoliosApi,
  listSecurities,
  optimizePortfolio,
  setCircuitBreakerConfig,
  upsertHolding,
  type KellyReport,
  type OptimizationResult,
  type PortfolioOut,
  type PortfolioSummaryOut,
  type RebalanceReport,
  type RiskMetrics,
} from "@/lib/api";
import { useActiveWeightVector } from "@/hooks/useActiveWeightVector";
import { fmtCompact, fmtNumber, fmtPercent, fmtScore } from "@/lib/format";
import { Panel } from "@/components/ui/Panel";
import { Stat } from "@/components/ui/Stat";
import { DataTable } from "@/components/ui/Table";
import { ConfidenceBadge, ThesisBadge } from "@/components/ui/Badges";
import { EmptyState, ErrorState, LoadingState } from "@/components/ui/States";
import { Pill } from "@/components/ui/Pill";
import { Button } from "@/components/ui/Button";
import { Label, NumberInput, Select, TextInput } from "@/components/ui/Input";
import { Tabs } from "@/components/ui/Tabs";

/**
 * Portfolio page — holdings-driven view.
 *
 * Lists the operator's portfolios in a picker, fetches the active
 * portfolio's analytic summary (sector / exchange exposure, holdings
 * with valuations, names needing review), and offers inline create-
 * portfolio and add/remove-holding affordances.
 *
 * The "no portfolios yet" path shows a creation form instead of the
 * holdings view so the page is useful from a clean install.
 */
export function PortfolioPage() {
  const portfolios = useQuery({
    queryKey: ["portfolios"],
    queryFn: listPortfoliosApi,
  });

  const [activeId, setActiveId] = useState<number | null>(null);
  const resolvedActive =
    activeId ?? (portfolios.data?.[0]?.id ?? null);

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-xl font-semibold tracking-tight">Portfolio</h1>
          <p className="text-2xs text-foreground-muted">
            Holdings-driven view. Switch portfolios with the picker; add or
            remove positions inline.
          </p>
        </div>
        {portfolios.data && portfolios.data.length > 0 && (
          <div className="flex items-center gap-2">
            <PortfolioPicker
              portfolios={portfolios.data}
              value={resolvedActive}
              onChange={setActiveId}
            />
            {resolvedActive !== null && (
              <DeletePortfolioButton
                portfolioId={resolvedActive}
                onDeleted={() => setActiveId(null)}
              />
            )}
          </div>
        )}
      </header>

      {portfolios.isLoading && <LoadingState />}
      {portfolios.data && portfolios.data.length === 0 && (
        <CreatePortfolioPanel
          onCreated={(p) => setActiveId(p.id)}
        />
      )}
      {resolvedActive !== null && (
        <PortfolioDetail portfolioId={resolvedActive} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Picker
// ---------------------------------------------------------------------------

function PortfolioPicker({
  portfolios,
  value,
  onChange,
}: {
  portfolios: PortfolioOut[];
  value: number | null;
  onChange: (id: number) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <Label htmlFor="portfolio-picker">Portfolio</Label>
      <Select
        id="portfolio-picker"
        value={value ?? ""}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-56"
      >
        {portfolios.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </Select>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Delete-portfolio button (inline confirmation)
// ---------------------------------------------------------------------------

function DeletePortfolioButton({
  portfolioId,
  onDeleted,
}: {
  portfolioId: number;
  onDeleted: () => void;
}) {
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);

  const del = useMutation({
    mutationFn: () => deletePortfolio(portfolioId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["portfolios"] });
      onDeleted();
      setConfirming(false);
    },
  });

  if (confirming) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-xs text-foreground-muted whitespace-nowrap">
          Delete this portfolio?
        </span>
        <Button
          variant="danger"
          size="sm"
          onClick={() => del.mutate()}
          disabled={del.isPending}
          loading={del.isPending}
        >
          Delete
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setConfirming(false)}
          disabled={del.isPending}
        >
          Cancel
        </Button>
      </div>
    );
  }

  return (
    <Button variant="ghost" size="sm" onClick={() => setConfirming(true)}>
      Delete
    </Button>
  );
}

// ---------------------------------------------------------------------------
// Create-portfolio panel (empty state)
// ---------------------------------------------------------------------------

function CreatePortfolioPanel({
  onCreated,
}: {
  onCreated: (p: PortfolioOut) => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [baseCurrency, setBaseCurrency] = useState("USD");

  const create = useMutation({
    mutationFn: () =>
      createPortfolio({
        name: name.trim(),
        description: description.trim() || null,
        base_currency: baseCurrency.trim() || "USD",
      }),
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: ["portfolios"] });
      onCreated(p);
      setName("");
      setDescription("");
    },
  });

  return (
    <Panel
      title="Create your first portfolio"
      description="Give it a name; you can add holdings on the next screen."
    >
      <form
        className="grid gap-3 sm:grid-cols-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (!name.trim()) return;
          create.mutate();
        }}
      >
        <div className="space-y-1">
          <Label htmlFor="new-portfolio-name">Name</Label>
          <TextInput
            id="new-portfolio-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Core long-only"
            required
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="new-portfolio-description">Description</Label>
          <TextInput
            id="new-portfolio-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="new-portfolio-currency">Base currency</Label>
          <TextInput
            id="new-portfolio-currency"
            value={baseCurrency}
            onChange={(e) => setBaseCurrency(e.target.value.toUpperCase())}
            maxLength={3}
            className="font-mono"
          />
        </div>
        <div className="sm:col-span-3">
          <Button
            variant="primary"
            type="submit"
            disabled={!name.trim() || create.isPending}
            loading={create.isPending}
          >
            Create portfolio
          </Button>
          {create.isError && (
            <span className="ml-3 text-xs text-danger">
              {(create.error as Error).message}
            </span>
          )}
        </div>
      </form>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Holdings detail — tabbed layout
// ---------------------------------------------------------------------------

type PortfolioTab = "overview" | "risk" | "optimizer" | "kelly" | "rebalance" | "circuit_breaker";

function PortfolioDetail({ portfolioId }: { portfolioId: number }) {
  const [tab, setTab] = useState<PortfolioTab>("overview");
  const { activeVector, activeId } = useActiveWeightVector();

  const summary = useQuery({
    queryKey: ["portfolio-summary", portfolioId, activeId],
    queryFn: () => getPortfolioSummary(portfolioId, activeId),
  });

  if (summary.isLoading) return <LoadingState />;
  if (!summary.data) {
    return <EmptyState title="No data" description="Couldn't load summary." />;
  }

  const s = summary.data;

  return (
    <>
      <CircuitBreakerBanner portfolioId={portfolioId} onConfigure={() => setTab("circuit_breaker")} />
      <KpiStrip summary={s} />
      {activeVector && (
        <p className="text-2xs text-foreground-muted">
          Scoring model:{" "}
          <Link to="/settings" className="font-medium text-foreground-secondary hover:text-foreground-primary">
            {activeVector.name} v.{activeVector.version}
          </Link>
          {" · "}change in{" "}
          <Link to="/settings" className="hover:text-foreground-primary underline underline-offset-2">
            Settings
          </Link>
        </p>
      )}

      <Tabs<PortfolioTab>
        value={tab}
        onChange={setTab}
        options={[
          { value: "overview", label: "Overview" },
          { value: "risk", label: "Risk" },
          { value: "optimizer", label: "Optimizer" },
          { value: "kelly", label: "Kelly" },
          { value: "rebalance", label: "Rebalance" },
          { value: "circuit_breaker", label: "Circuit Breaker" },
        ]}
      />

      {tab === "overview" && <OverviewTab portfolioId={portfolioId} summary={s} />}
      {tab === "risk" && <RiskTab portfolioId={portfolioId} />}
      {tab === "optimizer" && <OptimizerTab portfolioId={portfolioId} />}
      {tab === "kelly" && <KellyTab portfolioId={portfolioId} />}
      {tab === "rebalance" && <RebalanceTab portfolioId={portfolioId} />}
      {tab === "circuit_breaker" && <CircuitBreakerTab portfolioId={portfolioId} />}
    </>
  );
}

// ---------------------------------------------------------------------------
// Overview tab (existing content)
// ---------------------------------------------------------------------------

function OverviewTab({
  portfolioId,
  summary: s,
}: {
  portfolioId: number;
  summary: PortfolioSummaryOut;
}) {
  return (
    <>
      <div className="grid gap-4 lg:grid-cols-3">
        <Panel title="Sector exposure">
          {s.sector_exposure.length === 0 ? (
            <EmptyState title="No sector tags" />
          ) : (
            <Histogram rows={s.sector_exposure} />
          )}
        </Panel>
        <Panel title="Exchange mix">
          {s.exchange_exposure.length === 0 ? (
            <EmptyState title="No exchange data" />
          ) : (
            <Histogram rows={s.exchange_exposure} />
          )}
        </Panel>
        <Panel title="Names needing review" description="Insufficient confidence or deteriorating thesis.">
          <NeedsReview holdings={s.holdings} />
        </Panel>
      </div>
      <Panel title="Holdings" description="Click a ticker to open the company page." bodyClassName="px-0 py-0">
        <HoldingsTable portfolioId={portfolioId} holdings={s.holdings} />
      </Panel>
      <AddHoldingPanel portfolioId={portfolioId} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Circuit-breaker banner (G21 / E-5)
// ---------------------------------------------------------------------------
//
// Pinned above the KPI strip across every tab. Renders only when the
// portfolio is in SOFT_BREACH or HARD_BREACH so the operator can't miss
// it regardless of which tab they're on. The Configure call-to-action
// jumps the tab nav to the circuit-breaker editor.
//
// Per the operating rules the banner is decision-support only — it
// never auto-disables anything; the operator decides what to do.

function CircuitBreakerBanner({
  portfolioId,
  onConfigure,
}: {
  portfolioId: number;
  onConfigure: () => void;
}) {
  const status = useQuery({
    queryKey: ["circuit-breaker", portfolioId],
    queryFn: () => getCircuitBreakerStatus(portfolioId),
    // Refresh on focus so the banner doesn't get stale after the operator
    // looks at a different tab and comes back.
    refetchOnWindowFocus: true,
  });

  const s = status.data;
  if (!s) return null;
  if (s.status !== "SOFT_BREACH" && s.status !== "HARD_BREACH") return null;

  const isHard = s.status === "HARD_BREACH";
  const wrapperClass = isHard
    ? "border-danger/40 bg-danger-subtle"
    : "border-warning/40 bg-warning-subtle";
  const labelTone = isHard ? "bad" : "warn";

  return (
    <div
      className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border ${wrapperClass} px-4 py-3`}
      role="alert"
    >
      <div className="flex flex-wrap items-center gap-3">
        <Pill tone={labelTone} className="px-3 py-1 text-sm">
          {s.status.replace("_", " ")}
        </Pill>
        <div className="space-y-0.5">
          <p
            className={`text-sm font-medium ${
              isHard ? "text-danger" : "text-warning"
            }`}
          >
            {isHard
              ? "Hard drawdown threshold breached — manual review required."
              : "Soft drawdown threshold breached — consider reducing risk."}
          </p>
          <p className="text-2xs text-foreground-secondary">{s.message}</p>
        </div>
      </div>
      <Button
        variant={isHard ? "danger" : "secondary"}
        size="sm"
        onClick={onConfigure}
      >
        Open circuit-breaker
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Risk tab (G11)
// ---------------------------------------------------------------------------

function RiskTab({ portfolioId }: { portfolioId: number }) {
  const [lookback, setLookback] = useState(252);

  const risk = useQuery({
    queryKey: ["portfolio-risk", portfolioId, lookback],
    queryFn: () => getPortfolioRisk(portfolioId, { lookbackDays: lookback }),
  });

  return (
    <div className="space-y-4">
      <Panel
        title="Risk metrics"
        description={`Computed from ${lookback}d of adjusted closing prices.`}
        action={
          <div className="flex items-center gap-2">
            <Label htmlFor="risk-lookback">Lookback (days)</Label>
            <Select
              id="risk-lookback"
              value={lookback}
              onChange={(e) => setLookback(Number(e.target.value))}
              className="w-28"
            >
              <option value={63}>63 (3m)</option>
              <option value={126}>126 (6m)</option>
              <option value={252}>252 (1y)</option>
              <option value={504}>504 (2y)</option>
            </Select>
          </div>
        }
      >
        {risk.isLoading && <LoadingState />}
        {risk.isError && <ErrorState detail={(risk.error as Error).message} />}
        {risk.data && <RiskMetricsDisplay data={risk.data} />}
      </Panel>
    </div>
  );
}

function RiskMetricsDisplay({ data: d }: { data: RiskMetrics }) {
  const secIds = Object.keys(d.per_security_vol);
  return (
    <div className="space-y-4">
      {/* Portfolio-level KPIs */}
      <div className="grid gap-3 sm:grid-cols-4">
        <StatCard label="Ann. Volatility" value={d.portfolio_vol_ann !== null ? fmtPercent(d.portfolio_vol_ann) : "—"} tone={d.portfolio_vol_ann !== null && d.portfolio_vol_ann > 0.25 ? "bad" : d.portfolio_vol_ann !== null && d.portfolio_vol_ann > 0.15 ? "warn" : "default"} />
        <StatCard label="VaR 95% 1d" value={d.var_95_1d !== null ? fmtPercent(Math.abs(d.var_95_1d)) : "—"} hint="Daily loss not exceeded 95% of days" tone="muted" />
        <StatCard label="VaR 95% 10d" value={d.var_95_10d !== null ? fmtPercent(Math.abs(d.var_95_10d)) : "—"} hint="10-day parametric" tone="muted" />
        <StatCard label="Max Drawdown" value={d.max_drawdown !== null ? fmtPercent(Math.abs(d.max_drawdown)) : "—"} tone={d.max_drawdown !== null && Math.abs(d.max_drawdown) > 0.2 ? "bad" : d.max_drawdown !== null && Math.abs(d.max_drawdown) > 0.1 ? "warn" : "default"} />
      </div>

      {/* Per-security breakdown */}
      {secIds.length > 0 && (
        <DataTable
          dense
          rows={secIds}
          rowKey={(s) => s}
          columns={[
            { key: "id", header: "Security ID", cell: (s) => <code className="text-2xs">{s}</code> },
            { key: "vol", header: "Ann. Vol", align: "right", cell: (s) => fmtPercent(d.per_security_vol[s] ?? null) },
            { key: "beta", header: "Beta", align: "right", cell: (s) => fmtNumber(d.per_security_beta[s] ?? null) },
          ]}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Optimizer tab (G9)
// ---------------------------------------------------------------------------

function OptimizerTab({ portfolioId }: { portfolioId: number }) {
  const [mode, setMode] = useState<"equal_weight" | "min_variance" | "risk_parity" | "max_sharpe">("min_variance");
  const [lookback, setLookback] = useState(252);
  const [maxWeight, setMaxWeight] = useState(0.3);
  const [result, setResult] = useState<OptimizationResult | null>(null);

  const optimize = useMutation({
    mutationFn: () => optimizePortfolio(portfolioId, { mode, lookback_days: lookback, max_weight: maxWeight }),
    onSuccess: (data) => setResult(data),
  });

  return (
    <div className="space-y-4">
      <Panel title="Portfolio optimizer" description="Compute optimal weights using the selected method.">
        <div className="grid gap-3 sm:grid-cols-4">
          <div className="space-y-1">
            <Label>Method</Label>
            <Select value={mode} onChange={(e) => setMode(e.target.value as typeof mode)} className="w-full">
              <option value="equal_weight">Equal weight (1/N)</option>
              <option value="min_variance">Minimum variance</option>
              <option value="risk_parity">Risk parity (ERC)</option>
              <option value="max_sharpe">Maximum Sharpe</option>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Lookback (days)</Label>
            <Select value={lookback} onChange={(e) => setLookback(Number(e.target.value))} className="w-full">
              <option value={126}>126 (6m)</option>
              <option value={252}>252 (1y)</option>
              <option value={504}>504 (2y)</option>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Max weight</Label>
            <NumberInput min={0.05} max={1} step={0.05} value={maxWeight} onChange={(e) => setMaxWeight(Number(e.target.value))} />
          </div>
          <div className="flex items-end">
            <Button variant="primary" loading={optimize.isPending} onClick={() => optimize.mutate()} className="w-full">
              Optimize
            </Button>
          </div>
        </div>
        {optimize.isError && <ErrorState detail={(optimize.error as Error).message} />}
      </Panel>

      {result && (
        <Panel title={`Optimization result — ${result.mode}`} description={result.message ?? undefined}>
          <div className="grid gap-3 sm:grid-cols-3 mb-4">
            <StatCard label="Ann. Volatility" value={result.portfolio_vol_ann !== null ? fmtPercent(result.portfolio_vol_ann) : "—"} />
            <StatCard label="Expected Return" value={result.expected_return_ann !== null ? fmtPercent(result.expected_return_ann) : "—"} />
            <StatCard label="Sharpe Ratio" value={result.sharpe_ratio !== null ? fmtNumber(result.sharpe_ratio) : "—"} tone={result.sharpe_ratio !== null && result.sharpe_ratio > 1 ? "good" : result.sharpe_ratio !== null && result.sharpe_ratio > 0.5 ? "warn" : "bad"} />
          </div>
          <DataTable
            dense
            rows={Object.entries(result.weights).sort((a, b) => b[1] - a[1])}
            rowKey={([id]) => id}
            columns={[
              { key: "id", header: "Security ID", cell: ([id]) => <code className="text-2xs">{id}</code> },
              {
                key: "weight",
                header: "Weight",
                align: "right",
                cell: ([, w]) => (
                  <div className="flex items-center gap-2 justify-end">
                    <div className="h-1.5 w-16 overflow-hidden rounded-full bg-surface-base">
                      <div className="h-full bg-accent/70" style={{ width: `${Math.min(100, w * 100)}%` }} />
                    </div>
                    <span className="tabular-nums w-12 text-right">{fmtPercent(w)}</span>
                  </div>
                ),
              },
            ]}
          />
          <p className="mt-2 text-2xs text-foreground-muted">Solver: {result.solver_status}</p>
        </Panel>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Kelly tab (G10)
// ---------------------------------------------------------------------------

function KellyTab({ portfolioId }: { portfolioId: number }) {
  const kelly = useQuery({
    queryKey: ["portfolio-kelly", portfolioId],
    queryFn: () => getPortfolioKelly(portfolioId),
  });

  return (
    <div className="space-y-4">
      <Panel title="Kelly position sizer" description="Quarter-Kelly fractions with VIX-proxy scaling. Based on per-security win-rate and avg-win/avg-loss from historical daily returns.">
        {kelly.isLoading && <LoadingState />}
        {kelly.isError && <ErrorState detail={(kelly.error as Error).message} />}
        {kelly.data && <KellyDisplay data={kelly.data} />}
      </Panel>
    </div>
  );
}

function KellyDisplay({ data: d }: { data: KellyReport }) {
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <StatCard label="Portfolio Vol (ann.)" value={d.portfolio_vol_ann !== null ? fmtPercent(d.portfolio_vol_ann) : "—"} />
        <StatCard label="Vol target" value={fmtPercent(d.vol_target)} />
        <StatCard label="Max position" value={fmtPercent(d.max_position)} hint="Quarter-Kelly cap" />
      </div>
      {d.results.length === 0 ? (
        <EmptyState title="No Kelly results" description="Add holdings with price history." />
      ) : (
        <DataTable
          dense
          rows={[...d.results].sort((a, b) => b.vix_scaled - a.vix_scaled)}
          rowKey={(r) => r.security_id}
          columns={[
            { key: "ticker", header: "Security", cell: (r) => r.ticker ?? `#${r.security_id}` },
            { key: "win_rate", header: "Win rate", align: "right", cell: (r) => r.win_rate !== null ? fmtPercent(r.win_rate) : "—" },
            { key: "odds", header: "Odds (b)", align: "right", cell: (r) => r.odds_ratio !== null ? fmtNumber(r.odds_ratio) : "—" },
            { key: "full_kelly", header: "Full Kelly", align: "right", cell: (r) => fmtPercent(r.full_kelly) },
            { key: "qk", header: "Quarter Kelly", align: "right", cell: (r) => fmtPercent(r.quarter_kelly) },
            {
              key: "vix_scaled",
              header: "VIX-scaled",
              align: "right",
              cell: (r) => (
                <span className={r.insufficient_data ? "text-foreground-muted" : ""}>
                  {r.insufficient_data ? "—" : fmtPercent(r.vix_scaled)}
                </span>
              ),
            },
            { key: "flag", header: "", cell: (r) => r.insufficient_data ? <Pill tone="muted">insuff.</Pill> : null },
          ]}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Rebalance tab (G12)
// ---------------------------------------------------------------------------

function RebalanceTab({ portfolioId }: { portfolioId: number }) {
  const [portfolioValue, setPortfolioValue] = useState("");
  const [tcBps, setTcBps] = useState(50);
  const [minTradeBps, setMinTradeBps] = useState(50);
  const [report, setReport] = useState<RebalanceReport | null>(null);

  const run = useMutation({
    mutationFn: () =>
      getRebalanceProposal(portfolioId, {
        portfolioValue: portfolioValue ? Number(portfolioValue) : undefined,
        minTradeBps,
        tcBps,
      }),
    onSuccess: (data) => setReport(data),
  });

  return (
    <div className="space-y-4">
      <Panel title="Rebalance proposal" description="Diffs current weights vs. equal-weight target. Set portfolio value for notional sizing.">
        <div className="grid gap-3 sm:grid-cols-4">
          <div className="space-y-1">
            <Label htmlFor="rb-pv">Portfolio value (USD)</Label>
            <NumberInput id="rb-pv" step="any" value={portfolioValue} onChange={(e) => setPortfolioValue(e.target.value)} placeholder="optional" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="rb-tc">TC (bps)</Label>
            <NumberInput id="rb-tc" min={0} max={200} step={5} value={tcBps} onChange={(e) => setTcBps(Number(e.target.value))} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="rb-min">Min trade (bps)</Label>
            <NumberInput id="rb-min" min={0} max={500} step={10} value={minTradeBps} onChange={(e) => setMinTradeBps(Number(e.target.value))} />
          </div>
          <div className="flex items-end">
            <Button variant="primary" loading={run.isPending} onClick={() => run.mutate()} className="w-full">
              Generate
            </Button>
          </div>
        </div>
        {run.isError && <ErrorState detail={(run.error as Error).message} />}
      </Panel>

      {report && (
        <Panel title="Trade list">
          <div className="grid gap-3 sm:grid-cols-3 mb-4">
            <StatCard label="Turnover" value={fmtPercent(report.total_turnover)} />
            <StatCard label="Est. TC" value={report.estimated_tc_usd !== null ? `$${fmtNumber(report.estimated_tc_usd)}` : "—"} />
            <StatCard label="Trades" value={`${report.n_buys}B / ${report.n_sells}S`} />
          </div>
          <DataTable
            dense
            rows={report.trades.filter((t) => t.direction !== "hold")}
            rowKey={(t) => t.security_id}
            columns={[
              { key: "ticker", header: "Security", cell: (t) => t.ticker ?? `#${t.security_id}` },
              { key: "dir", header: "Dir", cell: (t) => <Pill tone={t.direction === "buy" ? "good" : "bad"}>{t.direction}</Pill> },
              { key: "current", header: "Current", align: "right", cell: (t) => fmtPercent(t.current_weight) },
              { key: "target", header: "Target", align: "right", cell: (t) => fmtPercent(t.target_weight) },
              { key: "delta", header: "Δ", align: "right", cell: (t) => <span className={t.delta_weight > 0 ? "text-success" : "text-danger"}>{t.delta_weight > 0 ? "+" : ""}{fmtPercent(t.delta_weight)}</span> },
              { key: "notional", header: "Notional", align: "right", cell: (t) => t.notional_usd !== null ? `$${fmtCompact(t.notional_usd)}` : "—" },
            ]}
          />
        </Panel>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Circuit-breaker tab (G21)
// ---------------------------------------------------------------------------

function CircuitBreakerTab({ portfolioId }: { portfolioId: number }) {
  const qc = useQueryClient();
  const [soft, setSoft] = useState("0.10");
  const [hard, setHard] = useState("0.20");

  const status = useQuery({
    queryKey: ["circuit-breaker", portfolioId],
    queryFn: () => getCircuitBreakerStatus(portfolioId),
  });

  const configure = useMutation({
    mutationFn: () =>
      setCircuitBreakerConfig(portfolioId, {
        soft_threshold: Number(soft),
        hard_threshold: Number(hard),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["circuit-breaker", portfolioId] });
    },
  });

  const remove = useMutation({
    mutationFn: () => setCircuitBreakerConfig(portfolioId, null),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["circuit-breaker", portfolioId] });
    },
  });

  const s = status.data;
  const statusTone =
    s?.status === "HARD_BREACH" ? "bad" :
    s?.status === "SOFT_BREACH" ? "warn" :
    s?.status === "OK" ? "good" : "muted";

  return (
    <div className="space-y-4">
      {/* Status */}
      <Panel title="Circuit-breaker status" description="Monitors portfolio drawdown from rolling peak.">
        {status.isLoading && <LoadingState />}
        {status.isError && <ErrorState detail={(status.error as Error).message} />}
        {s && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Pill tone={statusTone} className="text-sm px-3 py-1">{s.status.replace("_", " ")}</Pill>
              <span className="text-sm text-foreground-secondary">{s.message}</span>
            </div>
            {s.current_drawdown !== null && (
              <div className="grid gap-3 sm:grid-cols-3">
                <StatCard label="Current Drawdown" value={fmtPercent(Math.abs(s.current_drawdown))} tone={s.status === "HARD_BREACH" ? "bad" : s.status === "SOFT_BREACH" ? "warn" : "default"} />
                {s.soft_threshold !== null && <StatCard label="Soft threshold" value={fmtPercent(s.soft_threshold)} tone="muted" />}
                {s.hard_threshold !== null && <StatCard label="Hard threshold" value={fmtPercent(s.hard_threshold)} tone="muted" />}
              </div>
            )}
          </div>
        )}
      </Panel>

      {/* Config */}
      <Panel title="Configure thresholds" description="Drawdown fractions (e.g. 0.10 = 10%). Set to configure; remove to disable.">
        <div className="grid gap-3 sm:grid-cols-4">
          <div className="space-y-1">
            <Label htmlFor="cb-soft">Soft threshold</Label>
            <NumberInput id="cb-soft" min={0.01} max={0.99} step={0.01} value={soft} onChange={(e) => setSoft(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="cb-hard">Hard threshold</Label>
            <NumberInput id="cb-hard" min={0.01} max={0.99} step={0.01} value={hard} onChange={(e) => setHard(e.target.value)} />
          </div>
          <div className="flex items-end gap-2 sm:col-span-2">
            <Button variant="primary" loading={configure.isPending} onClick={() => configure.mutate()}>
              Save config
            </Button>
            <Button variant="ghost" loading={remove.isPending} onClick={() => remove.mutate()}>
              Remove
            </Button>
          </div>
        </div>
        {(configure.isError || remove.isError) && (
          <ErrorState detail={((configure.error ?? remove.error) as Error).message} />
        )}
      </Panel>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared stat card helper
// ---------------------------------------------------------------------------

function StatCard({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: React.ReactNode;
  hint?: string;
  tone?: "default" | "good" | "warn" | "bad" | "muted";
}) {
  const colorCls =
    tone === "good" ? "text-success" :
    tone === "warn" ? "text-warning" :
    tone === "bad" ? "text-danger" :
    tone === "muted" ? "text-foreground-muted" :
    "text-foreground-primary";
  return (
    <div className="rounded-md border border-border-subtle bg-surface-subtle p-3">
      <p className="text-2xs uppercase tracking-wider text-foreground-muted">{label}</p>
      <p className={`mt-1 text-xl font-semibold tabular-nums ${colorCls}`}>{value}</p>
      {hint && <p className="mt-0.5 text-2xs text-foreground-muted">{hint}</p>}
    </div>
  );
}

function KpiStrip({ summary: s }: { summary: PortfolioSummaryOut }) {
  const hhi = s.concentration_index;
  const hhiTone: "default" | "warn" | "bad" =
    hhi === null ? "default" : hhi > 0.25 ? "bad" : hhi > 0.10 ? "warn" : "default";

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
      <Panel>
        <Stat label="Holdings" value={s.n_holdings} />
      </Panel>
      <Panel>
        <Stat
          label={`Mkt value (${s.base_currency})`}
          value={s.market_value !== null ? fmtCompact(s.market_value) : "—"}
        />
      </Panel>
      <Panel>
        <Stat
          label="Avg composite"
          value={s.avg_composite !== null ? fmtScore(s.avg_composite) : "—"}
        />
      </Panel>
      <Panel>
        <Stat
          label="Concentration"
          value={hhi !== null ? fmtPercent(hhi) : "—"}
          hint="Herfindahl index — lower is more diversified"
          tone={hhiTone}
        />
      </Panel>
      <Panel>
        <Stat
          label="Insufficient"
          value={s.insufficient_count}
          tone={s.insufficient_count > 0 ? "warn" : "default"}
        />
      </Panel>
      <Panel>
        <Stat
          label="Deteriorating"
          value={s.deteriorating_count}
          tone={s.deteriorating_count > 0 ? "bad" : "default"}
        />
      </Panel>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Holdings table + delete action
// ---------------------------------------------------------------------------

type HoldingRow = PortfolioSummaryOut["holdings"][number];

function HoldingsTable({
  portfolioId,
  holdings,
}: {
  portfolioId: number;
  holdings: HoldingRow[];
}) {
  const qc = useQueryClient();
  const remove = useMutation({
    mutationFn: ({ securityId, asOf }: { securityId: number; asOf: string }) =>
      deleteHolding(portfolioId, securityId, asOf),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["portfolio-summary", portfolioId] });
    },
  });

  if (holdings.length === 0) {
    return (
      <EmptyState
        title="No holdings yet"
        description="Use the form below to add a position."
      />
    );
  }

  return (
    <DataTable
      dense
      rows={[...holdings].sort(
        (a, b) => (b.market_value ?? 0) - (a.market_value ?? 0),
      )}
      rowKey={(r) => `${r.security_id}`}
      empty="No holdings."
      columns={[
        {
          key: "ticker",
          header: "Ticker",
          cell: (r) =>
            r.ticker ? (
              <Link
                to={`/company/${r.security_id}`}
                className="font-medium text-foreground-primary hover:text-accent-strong"
              >
                {r.ticker}
              </Link>
            ) : (
              `#${r.security_id}`
            ),
        },
        {
          key: "shares",
          header: "Shares",
          align: "right",
          cell: (r) => fmtCompact(r.shares),
        },
        {
          key: "cost_basis",
          header: "Cost basis",
          align: "right",
          cell: (r) => (r.cost_basis !== null ? fmtCompact(r.cost_basis) : "—"),
        },
        {
          key: "avg_cost",
          header: "Avg cost",
          align: "right",
          cell: (r) => (r.avg_cost !== null ? fmtCompact(r.avg_cost) : "—"),
        },
        {
          key: "mv",
          header: "Mkt value",
          align: "right",
          cell: (r) => (r.market_value !== null ? fmtCompact(r.market_value) : "—"),
        },
        {
          key: "weight",
          header: "Weight",
          align: "right",
          cell: (r) => (r.weight !== null ? fmtPercent(r.weight) : "—"),
        },
        {
          key: "composite",
          header: "Composite",
          align: "right",
          cell: (r) => fmtScore(r.composite),
        },
        {
          key: "confidence",
          header: "Confidence",
          cell: (r) =>
            r.confidence_band ? (
              <ConfidenceBadge band={r.confidence_band as never} />
            ) : (
              "—"
            ),
        },
        {
          key: "thesis",
          header: "Thesis",
          cell: (r) =>
            r.thesis_status ? (
              <ThesisBadge status={r.thesis_status as never} />
            ) : (
              "—"
            ),
        },
        {
          key: "notes",
          header: "Notes",
          cell: (r) => (
            <span className="max-w-40 truncate text-foreground-muted" title={r.notes ?? undefined}>
              {r.notes ?? "—"}
            </span>
          ),
        },
        {
          key: "actions",
          header: "",
          align: "right",
          cell: (r) => (
            <Button
              variant="ghost"
              size="sm"
              disabled={remove.isPending || !r.as_of}
              title={r.as_of ? undefined : "row missing as_of — refresh to retry"}
              onClick={() => {
                if (!r.as_of) return;
                remove.mutate({
                  securityId: r.security_id,
                  asOf: r.as_of,
                });
              }}
            >
              Remove
            </Button>
          ),
        },
      ]}
    />
  );
}

function NeedsReview({ holdings }: { holdings: HoldingRow[] }) {
  const flagged = holdings.filter(
    (h) =>
      h.confidence_band === "insufficient" || h.thesis_status === "deteriorating",
  );
  if (flagged.length === 0) {
    return <EmptyState title="No flags." />;
  }
  return (
    <ul className="space-y-1.5 text-xs">
      {flagged.map((h) => (
        <li key={h.security_id} className="flex items-center gap-2">
          <Link
            to={`/company/${h.security_id}`}
            className="font-mono font-medium text-foreground-primary hover:text-accent-strong"
          >
            {h.ticker ?? `#${h.security_id}`}
          </Link>
          {h.confidence_band === "insufficient" && (
            <ConfidenceBadge band="insufficient" />
          )}
          {h.thesis_status === "deteriorating" && (
            <ThesisBadge status="deteriorating" />
          )}
          <span className="ml-auto tabular-nums text-foreground-muted">
            {h.weight !== null ? fmtPercent(h.weight) : "—"}
          </span>
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Add-holding form
// ---------------------------------------------------------------------------

function AddHoldingPanel({ portfolioId }: { portfolioId: number }) {
  const qc = useQueryClient();
  const universe = useQuery({
    queryKey: ["portfolio-universe-picker"],
    queryFn: () => listSecurities({ limit: 1000 }),
  });

  const today = new Date().toISOString().slice(0, 10);
  const [securityId, setSecurityId] = useState<number | null>(null);
  const [asOf, setAsOf] = useState<string>(today);
  const [shares, setShares] = useState<string>("");
  const [costBasis, setCostBasis] = useState<string>("");
  const [avgCost, setAvgCost] = useState<string>("");
  const [notes, setNotes] = useState<string>("");

  const upsert = useMutation({
    mutationFn: () =>
      upsertHolding(portfolioId, {
        security_id: securityId!,
        as_of: asOf,
        shares: Number(shares),
        cost_basis: costBasis ? Number(costBasis) : null,
        avg_cost: avgCost ? Number(avgCost) : null,
        notes: notes || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["portfolio-summary", portfolioId] });
      setSecurityId(null);
      setShares("");
      setCostBasis("");
      setAvgCost("");
      setNotes("");
    },
  });

  const canSubmit =
    securityId !== null && shares.trim() !== "" && Number(shares) > 0;

  return (
    <Panel
      title="Add or update a holding"
      description="Same (security, as-of) pair is upserted — re-submit to revise."
    >
      <form
        className="grid gap-3 sm:grid-cols-6"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) upsert.mutate();
        }}
      >
        <div className="space-y-1 sm:col-span-2">
          <Label htmlFor="holding-security">Security</Label>
          <Select
            id="holding-security"
            value={securityId ?? ""}
            onChange={(e) => setSecurityId(e.target.value ? Number(e.target.value) : null)}
            disabled={universe.isLoading}
            required
          >
            <option value="">Select…</option>
            {universe.data?.map((s) => (
              <option key={s.id} value={s.id}>
                {s.ticker} — {s.name}
              </option>
            ))}
          </Select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="holding-as-of">As of</Label>
          <TextInput
            id="holding-as-of"
            type="date"
            value={asOf}
            onChange={(e) => setAsOf(e.target.value)}
            required
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="holding-shares">Shares</Label>
          <NumberInput
            id="holding-shares"
            min={0}
            step="any"
            value={shares}
            onChange={(e) => setShares(e.target.value)}
            required
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="holding-cost-basis">Cost basis</Label>
          <NumberInput
            id="holding-cost-basis"
            step="any"
            value={costBasis}
            onChange={(e) => setCostBasis(e.target.value)}
            placeholder="optional"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="holding-avg-cost">Avg cost</Label>
          <NumberInput
            id="holding-avg-cost"
            step="any"
            value={avgCost}
            onChange={(e) => setAvgCost(e.target.value)}
            placeholder="optional"
          />
        </div>
        <div className="space-y-1 sm:col-span-5">
          <Label htmlFor="holding-notes">Notes</Label>
          <TextInput
            id="holding-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional"
          />
        </div>
        <div className="flex items-end sm:col-span-1">
          <Button
            variant="primary"
            type="submit"
            disabled={!canSubmit || upsert.isPending}
            loading={upsert.isPending}
            className="w-full"
          >
            Save
          </Button>
        </div>
        {upsert.isError && (
          <div className="sm:col-span-6 text-xs text-danger">
            {(upsert.error as Error).message}
          </div>
        )}
      </form>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Histogram
// ---------------------------------------------------------------------------

function Histogram({
  rows,
}: {
  rows: { bucket: string; weight: number; n_securities: number }[];
}) {
  return (
    <ul className="space-y-2 text-xs">
      {rows.map((row) => {
        const pct = row.weight * 100;
        return (
          <li key={row.bucket} className="space-y-0.5">
            <div className="flex items-center justify-between">
              <span className="font-mono text-foreground-secondary">
                {row.bucket}
              </span>
              <span className="tabular-nums text-foreground-muted">
                {row.n_securities} <Pill tone="muted">{pct.toFixed(0)}%</Pill>
              </span>
            </div>
            <div className="h-1 w-full overflow-hidden rounded-full bg-surface-base">
              <div
                className="h-full bg-accent/70"
                style={{ width: `${Math.min(100, pct)}%` }}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
}
